/**
 * @fileoverview Generic TodoListSection Component
 *
 * Universal todo list component that works with any entity type.
 * Features drag & drop reordering and "niet van toepassing" status.
 */

import React, { useState, useEffect } from "react";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { TodoItem, TodoPriority } from "shared/types/todo-item";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ListChecks,
  Plus,
  Trash,
  Star,
  PencilSimple,
  X,
  Check,
  DotsSixVertical,
  ProhibitInset,
  DotsThree,
  CaretDown,
} from "@phosphor-icons/react";
import { UserSelection } from "shared/components/userSelection/userSelection";
import { ClerkUser } from "shared/types/user";
import { useUsers } from "shared/contexts/users.context";
import * as Sentry from "@sentry/react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface TodoListSectionProps {
  entityType: string; // e.g., 'bug-report', 'renovation-project'
  entityId?: number; // Can be provided directly
  entity?: any; // Or extracted from entity object (from UniversalDetailPage)
  title?: string; // Optional custom title
  className?: string;
  collapsible?: boolean; // Whether section can be collapsed
  defaultCollapsed?: boolean; // Default collapsed state
}

export const TodoListSection: React.FC<TodoListSectionProps> = ({
  entityType,
  entityId: providedEntityId,
  entity,
  title = "Todo Lijst",
  className = "",
  collapsible = false,
  defaultCollapsed = false,
}) => {
  // Extract entityId from either prop or entity object
  const entityId = providedEntityId || entity?.id;

  if (!entityId) {
    return (
      <div
        className={`rounded-lg border border-neutral-200 bg-white p-4 ${className}`}
      >
        <div className="text-center text-neutral-500">Entity ID ontbreekt</div>
      </div>
    );
  }
  const { session } = useSession();
  const { users } = useUsers();
  const [todos, setTodos] = useState<TodoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  // New todo form state
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newPriority, setNewPriority] = useState<TodoPriority>(
    TodoPriority.MEDIUM,
  );
  const [newAssignee, setNewAssignee] = useState<ClerkUser | null>(null);

  // Edit todo form state
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editPriority, setEditPriority] = useState<TodoPriority>(
    TodoPriority.MEDIUM,
  );
  const [editAssignee, setEditAssignee] = useState<ClerkUser | null>(null);

  // Drag & Drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const fetchTodos = async () => {
    setLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/todos?entityType=${entityType}&entityId=${entityId}`,
        { method: "GET" },
        session,
      );

      if (response.ok) {
        const data = await response.json();
        setTodos(data);
      } else {
        throw new Error("Failed to fetch todos");
      }
    } catch (error) {
      console.error("Error fetching todos:", error);
      Sentry.captureException(error, {
        tags: { component: "TodoListSection" },
        extra: { entityType, entityId },
      });
      toast.error("Fout bij laden van todos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (session) {
      fetchTodos();
    }
  }, [entityType, entityId, session]);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = todos.findIndex((t) => t.id === active.id);
      const newIndex = todos.findIndex((t) => t.id === over.id);

      const newTodos = arrayMove(todos, oldIndex, newIndex);
      setTodos(newTodos);

      // Update order on backend
      try {
        await fetchWithAuth(
          `${API_URL}/todos/reorder`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              entityType,
              entityId,
              todoIds: newTodos.map((t) => t.id),
            }),
          },
          session,
        );
      } catch (error) {
        console.error("Error reordering todos:", error);
        toast.error("Fout bij opslaan volgorde");
        // Revert on error
        fetchTodos();
      }
    }
  };

  const handleToggleComplete = async (todoId: number, isCompleted: boolean) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/todos/${todoId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            isCompleted: !isCompleted,
            isNotApplicable: false,
          }),
        },
        session,
      );

      if (response.ok) {
        const updatedTodo = await response.json();
        setTodos(todos.map((t) => (t.id === todoId ? updatedTodo : t)));
      } else {
        throw new Error("Failed to update todo");
      }
    } catch (error) {
      console.error("Error updating todo:", error);
      Sentry.captureException(error, {
        tags: { component: "TodoListSection" },
        extra: { todoId },
      });
      toast.error("Fout bij updaten van todo");
    }
  };

  const handleSetNotApplicable = async (
    todoId: number,
    isNotApplicable: boolean,
  ) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/todos/${todoId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            isNotApplicable: !isNotApplicable,
            isCompleted: false,
          }),
        },
        session,
      );

      if (response.ok) {
        const updatedTodo = await response.json();
        setTodos(todos.map((t) => (t.id === todoId ? updatedTodo : t)));
        toast.success(
          isNotApplicable
            ? "Teruggezet naar open"
            : "Gemarkeerd als niet van toepassing",
        );
      } else {
        throw new Error("Failed to update todo");
      }
    } catch (error) {
      console.error("Error updating todo:", error);
      Sentry.captureException(error, {
        tags: { component: "TodoListSection" },
        extra: { todoId },
      });
      toast.error("Fout bij updaten van todo");
    }
  };

  const handleResetTodo = async (todoId: number) => {
    try {
      const response = await fetchWithAuth(
        `${API_URL}/todos/${todoId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ isCompleted: false, isNotApplicable: false }),
        },
        session,
      );

      if (response.ok) {
        const updatedTodo = await response.json();
        setTodos(todos.map((t) => (t.id === todoId ? updatedTodo : t)));
        toast.success("Teruggezet naar open");
      } else {
        throw new Error("Failed to update todo");
      }
    } catch (error) {
      console.error("Error updating todo:", error);
      toast.error("Fout bij updaten van todo");
    }
  };

  const handleAddTodo = async () => {
    if (!newTitle.trim()) {
      toast.error("Titel is verplicht");
      return;
    }

    // Ensure entityId is a number
    const numericEntityId = typeof entityId === 'string' ? parseInt(entityId, 10) : entityId;

    if (!numericEntityId || isNaN(numericEntityId)) {
      toast.error("Ongeldig entity ID");
      console.error("Invalid entityId:", entityId);
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${API_URL}/todos`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: newTitle.trim(),
            description: newDescription.trim() || undefined,
            priority: newPriority,
            assignedTo: newAssignee?.id || undefined,
            entityType,
            entityId: numericEntityId,
            order: todos.length,
          }),
        },
        session,
      );

      if (response.ok) {
        const newTodo = await response.json();
        setTodos([...todos, newTodo]);
        setNewTitle("");
        setNewDescription("");
        setNewPriority(TodoPriority.MEDIUM);
        setNewAssignee(null);
        setIsAddingNew(false);
        toast.success("Todo toegevoegd");
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error("Failed to create todo:", {
          status: response.status,
          statusText: response.statusText,
          error: errorData,
          payload: {
            title: newTitle.trim(),
            description: newDescription.trim() || undefined,
            priority: newPriority,
            assignedTo: newAssignee?.id || undefined,
            entityType,
            entityId,
            order: todos.length,
          }
        });
        throw new Error(errorData.message || "Failed to create todo");
      }
    } catch (error) {
      console.error("Error creating todo:", error);
      Sentry.captureException(error, {
        tags: { component: "TodoListSection" },
        extra: { entityType, entityId },
      });
      toast.error("Fout bij aanmaken van todo");
    }
  };

  const handleStartEdit = (todo: TodoItem) => {
    setEditingId(todo.id);
    setEditTitle(todo.title);
    setEditDescription(todo.description || "");
    setEditPriority(todo.priority);

    if (todo.assignedTo && users) {
      const assignee = users.find((u) => u.id === todo.assignedTo);
      setEditAssignee(assignee || null);
    } else {
      setEditAssignee(null);
    }
  };

  const handleSaveEdit = async (todoId: number) => {
    if (!editTitle.trim()) {
      toast.error("Titel is verplicht");
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${API_URL}/todos/${todoId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            title: editTitle.trim(),
            description: editDescription.trim() || undefined,
            priority: editPriority,
            assignedTo: editAssignee?.id || undefined,
          }),
        },
        session,
      );

      if (response.ok) {
        const updatedTodo = await response.json();
        setTodos(todos.map((t) => (t.id === todoId ? updatedTodo : t)));
        setEditingId(null);
        toast.success("Todo bijgewerkt");
      } else {
        throw new Error("Failed to update todo");
      }
    } catch (error) {
      console.error("Error updating todo:", error);
      Sentry.captureException(error, {
        tags: { component: "TodoListSection" },
        extra: { todoId },
      });
      toast.error("Fout bij updaten van todo");
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditTitle("");
    setEditDescription("");
    setEditPriority(TodoPriority.MEDIUM);
    setEditAssignee(null);
  };

  const handleDeleteTodo = async (todoId: number) => {
    if (!window.confirm("Weet je zeker dat je deze todo wilt verwijderen?")) {
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${API_URL}/todos/${todoId}`,
        { method: "DELETE" },
        session,
      );

      if (response.ok) {
        setTodos(todos.filter((t) => t.id !== todoId));
        toast.success("Todo verwijderd");
      } else {
        throw new Error("Failed to delete todo");
      }
    } catch (error) {
      console.error("Error deleting todo:", error);
      Sentry.captureException(error, {
        tags: { component: "TodoListSection" },
        extra: { todoId },
      });
      toast.error("Fout bij verwijderen van todo");
    }
  };

  const getPriorityColor = (priority: TodoPriority) => {
    switch (priority) {
      case TodoPriority.LOW:
        return "text-neutral-400";
      case TodoPriority.MEDIUM:
        return "text-blue-500";
      case TodoPriority.HIGH:
        return "text-orange-500";
      case TodoPriority.CRITICAL:
        return "text-brick-primary";
      default:
        return "text-neutral-400";
    }
  };

  const getPriorityStars = (priority: TodoPriority) => {
    switch (priority) {
      case TodoPriority.LOW:
        return 1;
      case TodoPriority.MEDIUM:
        return 2;
      case TodoPriority.HIGH:
        return 3;
      case TodoPriority.CRITICAL:
        return 4;
      default:
        return 2;
    }
  };

  const getUserName = (userId: string) => {
    if (!users) return "Laden...";
    const user = users.find((u) => u.id === userId);
    return user ? `${user.first_name} ${user.last_name}` : "Onbekend";
  };

  if (loading) {
    return (
      <div
        className={`rounded-lg border border-neutral-200 bg-white p-4 ${className}`}
      >
        <div className="text-center text-neutral-500">Todos laden...</div>
      </div>
    );
  }

  const completedCount = todos.filter(
    (t) => t.isCompleted || t.isNotApplicable,
  ).length;
  const totalCount = todos.length;

  return (
    <div
      className={`rounded-lg border border-neutral-200 bg-white shadow-sm ${className}`}
    >
      <div
        className={`px-4 py-3 ${!isCollapsed ? "border-b border-neutral-200" : ""} z-10 bg-white ${collapsible ? "cursor-pointer transition-colors hover:bg-neutral-50" : ""}`}
        onClick={collapsible ? () => setIsCollapsed(!isCollapsed) : undefined}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {collapsible && (
              <CaretDown
                size={16}
                className={`text-neutral-600 transition-transform ${isCollapsed ? "-rotate-90" : ""}`}
                weight="bold"
              />
            )}
            <ListChecks
              size={18}
              className="text-moss-primary"
              weight="duotone"
            />
            <h3 className="text-neutral-primary text-base font-semibold">
              {title}
            </h3>
            <Badge variant="outline" className="text-xs">
              {completedCount} / {totalCount}
            </Badge>
          </div>
          {!isCollapsed && !isAddingNew && (
            <Button
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setIsAddingNew(true);
              }}
            >
              <Plus size={16} className="mr-1" />
              Nieuw
            </Button>
          )}
        </div>
      </div>

      {!isCollapsed && (
        <div className="space-y-3 p-4">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={todos.map((t) => t.id)}
              strategy={verticalListSortingStrategy}
            >
              {todos.map((todo, index) => (
                <SortableTodoItem
                  key={todo.id}
                  todo={todo}
                  orderNumber={index + 1}
                  editingId={editingId}
                  editTitle={editTitle}
                  editDescription={editDescription}
                  editPriority={editPriority}
                  editAssignee={editAssignee}
                  onSetEditTitle={setEditTitle}
                  onSetEditDescription={setEditDescription}
                  onSetEditPriority={setEditPriority}
                  onSetEditAssignee={setEditAssignee}
                  onStartEdit={handleStartEdit}
                  onSaveEdit={handleSaveEdit}
                  onCancelEdit={handleCancelEdit}
                  onToggleComplete={handleToggleComplete}
                  onSetNotApplicable={handleSetNotApplicable}
                  onResetTodo={handleResetTodo}
                  onDelete={handleDeleteTodo}
                  getPriorityColor={getPriorityColor}
                  getPriorityStars={getPriorityStars}
                  getUserName={getUserName}
                />
              ))}
            </SortableContext>
          </DndContext>

          {/* Add New Todo Form */}
          {isAddingNew && (
            <div className="border-moss-primary bg-moss-primary/5 rounded-lg border-2 border-dashed p-3">
              <div className="space-y-3">
                <Input
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="Todo titel"
                  autoFocus
                />
                <Textarea
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  placeholder="Beschrijving (optioneel)"
                  rows={2}
                />
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label className="text-xs text-neutral-600">
                      Prioriteit
                    </label>
                    <Select
                      value={newPriority}
                      onValueChange={(value) =>
                        setNewPriority(value as TodoPriority)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={TodoPriority.LOW}>Laag</SelectItem>
                        <SelectItem value={TodoPriority.MEDIUM}>
                          Normaal
                        </SelectItem>
                        <SelectItem value={TodoPriority.HIGH}>Hoog</SelectItem>
                        <SelectItem value={TodoPriority.CRITICAL}>
                          Kritiek
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs text-neutral-600">
                      Toegewezen aan
                    </label>
                    <div className="flex items-center gap-2 rounded-md border bg-white p-2">
                      <UserSelection
                        defaultUser={newAssignee}
                        onUserSelect={setNewAssignee}
                      />
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleAddTodo}>
                    <Plus size={16} className="mr-1" />
                    Toevoegen
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setIsAddingNew(false);
                      setNewTitle("");
                      setNewDescription("");
                      setNewPriority(TodoPriority.MEDIUM);
                      setNewAssignee(null);
                    }}
                  >
                    Annuleren
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Empty State */}
          {todos.length === 0 && !isAddingNew && (
            <div className="py-8 text-center text-neutral-500">
              <ListChecks
                size={32}
                className="mx-auto mb-2 opacity-50"
                weight="duotone"
              />
              <p className="text-sm">Nog geen todos</p>
              <p className="mt-1 text-xs">
                Klik op "Nieuw" om een todo toe te voegen
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Sortable Todo Item Component
interface SortableTodoItemProps {
  todo: TodoItem;
  orderNumber: number;
  editingId: number | null;
  editTitle: string;
  editDescription: string;
  editPriority: TodoPriority;
  editAssignee: ClerkUser | null;
  onSetEditTitle: (value: string) => void;
  onSetEditDescription: (value: string) => void;
  onSetEditPriority: (value: TodoPriority) => void;
  onSetEditAssignee: (value: ClerkUser | null) => void;
  onStartEdit: (todo: TodoItem) => void;
  onSaveEdit: (id: number) => void;
  onCancelEdit: () => void;
  onToggleComplete: (id: number, isCompleted: boolean) => void;
  onSetNotApplicable: (id: number, isNotApplicable: boolean) => void;
  onResetTodo: (id: number) => void;
  onDelete: (id: number) => void;
  getPriorityColor: (priority: TodoPriority) => string;
  getPriorityStars: (priority: TodoPriority) => number;
  getUserName: (userId: string) => string;
}

// Simple custom dropdown component without portals
interface CustomDropdownProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  trigger,
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <div onClick={() => setIsOpen(!isOpen)}>{trigger}</div>
      {isOpen && (
        <div className="absolute top-full right-0 z-50 mt-1 w-48 rounded-md border border-neutral-200 bg-white shadow-lg">
          <div className="py-1">{children}</div>
        </div>
      )}
    </div>
  );
};

interface CustomDropdownItemProps {
  onClick: () => void;
  children: React.ReactNode;
  className?: string;
}

const CustomDropdownItem: React.FC<CustomDropdownItemProps> = ({
  onClick,
  children,
  className = "",
}) => {
  const handleClick = () => {
    onClick();
  };

  return (
    <button
      type="button"
      className={`flex w-full items-center px-3 py-2 text-left text-sm transition-colors hover:bg-neutral-100 ${className}`}
      onClick={handleClick}
    >
      {children}
    </button>
  );
};

const SortableTodoItem: React.FC<SortableTodoItemProps> = ({
  todo,
  orderNumber,
  editingId,
  editTitle,
  editDescription,
  editPriority,
  editAssignee,
  onSetEditTitle,
  onSetEditDescription,
  onSetEditPriority,
  onSetEditAssignee,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onToggleComplete,
  onSetNotApplicable,
  onResetTodo,
  onDelete,
  getPriorityColor,
  getPriorityStars,
  getUserName,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: todo.id });

  const style: React.CSSProperties = {
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  if (isDragging && transform) {
    style.transform = CSS.Transform.toString(transform);
  }

  // Determine visual styling based on status
  const isNotApplicable = todo.isNotApplicable;
  const isCompleted = todo.isCompleted;

  let containerClass = "bg-white border-neutral-300";
  let textClass = "";
  let icon = null;

  if (isNotApplicable) {
    containerClass = "bg-orange-50 border-orange-200";
    textClass = "line-through text-neutral-500 opacity-60";
    icon = (
      <ProhibitInset size={16} weight="fill" className="text-orange-500" />
    );
  } else if (isCompleted) {
    containerClass = "bg-neutral-50 border-neutral-200";
    textClass = "line-through text-neutral-500";
    icon = <Check size={16} weight="bold" className="text-moss-primary" />;
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`rounded-lg border p-3 transition-all ${containerClass}`}
    >
      {editingId === todo.id ? (
        // Edit Mode
        <div className="space-y-3">
          <Input
            value={editTitle}
            onChange={(e) => onSetEditTitle(e.target.value)}
            placeholder="Todo titel"
          />
          <Textarea
            value={editDescription}
            onChange={(e) => onSetEditDescription(e.target.value)}
            placeholder="Beschrijving (optioneel)"
            rows={2}
          />
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <label className="text-xs text-neutral-600">Prioriteit</label>
              <Select
                value={editPriority}
                onValueChange={(value) =>
                  onSetEditPriority(value as TodoPriority)
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={TodoPriority.LOW}>Laag</SelectItem>
                  <SelectItem value={TodoPriority.MEDIUM}>Normaal</SelectItem>
                  <SelectItem value={TodoPriority.HIGH}>Hoog</SelectItem>
                  <SelectItem value={TodoPriority.CRITICAL}>Kritiek</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-xs text-neutral-600">Toegewezen aan</label>
              <div className="flex items-center gap-2 rounded-md border p-2">
                <UserSelection
                  defaultUser={editAssignee}
                  onUserSelect={onSetEditAssignee}
                />
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => onSaveEdit(todo.id)}>
              <Check size={16} className="mr-1" />
              Opslaan
            </Button>
            <Button size="sm" variant="outline" onClick={onCancelEdit}>
              <X size={16} className="mr-1" />
              Annuleren
            </Button>
          </div>
        </div>
      ) : (
        // View Mode
        <div className="flex items-start gap-3">
          <div className="flex items-center gap-2 pt-0.5">
            <div className="flex items-center gap-1">
              <span className="min-w-[20px] text-xs font-semibold text-neutral-400">
                {orderNumber}.
              </span>
              <div
                {...attributes}
                {...listeners}
                className="cursor-grab active:cursor-grabbing"
              >
                <DotsSixVertical size={16} className="text-neutral-400" />
              </div>
            </div>
            <Checkbox
              checked={isCompleted || isNotApplicable}
              onCheckedChange={() =>
                onToggleComplete(todo.id, todo.isCompleted)
              }
              disabled={isNotApplicable}
            />
            {icon && <div className="ml-1">{icon}</div>}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className={`text-sm font-medium ${textClass}`}>
                    {todo.title}
                  </h4>
                  {isNotApplicable && (
                    <Badge
                      variant="outline"
                      className="border-orange-300 bg-orange-100 text-xs text-orange-700"
                    >
                      N.v.t.
                    </Badge>
                  )}
                </div>
                {todo.description && (
                  <p
                    className={`mt-1 text-xs text-neutral-600 ${isNotApplicable ? "opacity-60" : ""}`}
                  >
                    {todo.description}
                  </p>
                )}
              </div>

              <CustomDropdown
                trigger={
                  <button
                    type="button"
                    className="inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors hover:bg-neutral-100"
                  >
                    <DotsThree
                      size={16}
                      weight="bold"
                      className="text-neutral-600"
                    />
                  </button>
                }
              >
                {!isCompleted && !isNotApplicable && (
                  <>
                    <CustomDropdownItem
                      onClick={() => onToggleComplete(todo.id, false)}
                    >
                      <Check size={14} className="mr-2" />
                      Markeer als voltooid
                    </CustomDropdownItem>
                    <CustomDropdownItem
                      onClick={() => onSetNotApplicable(todo.id, false)}
                    >
                      <ProhibitInset size={14} className="mr-2" />
                      Niet van toepassing
                    </CustomDropdownItem>
                  </>
                )}
                {(isCompleted || isNotApplicable) && (
                  <CustomDropdownItem onClick={() => onResetTodo(todo.id)}>
                    <X size={14} className="mr-2" />
                    Zet terug naar open
                  </CustomDropdownItem>
                )}
                <CustomDropdownItem onClick={() => onStartEdit(todo)}>
                  <PencilSimple size={14} className="mr-2" />
                  Bewerken
                </CustomDropdownItem>
                <CustomDropdownItem
                  onClick={() => onDelete(todo.id)}
                  className="text-brick-primary"
                >
                  <Trash size={14} className="mr-2" />
                  Verwijderen
                </CustomDropdownItem>
              </CustomDropdown>
            </div>

            <div className="mt-2 flex items-center gap-3">
              {/* Priority Stars */}
              <div className="flex items-center gap-0.5">
                {Array.from({ length: 4 }).map((_, i) => (
                  <Star
                    key={i}
                    size={12}
                    weight={
                      i < getPriorityStars(todo.priority) ? "fill" : "regular"
                    }
                    className={getPriorityColor(todo.priority)}
                  />
                ))}
              </div>

              {/* Assignee */}
              {todo.assignedTo && (
                <Badge variant="outline" className="text-xs">
                  {getUserName(todo.assignedTo)}
                </Badge>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
